import { getField, Locales } from "@simplyk/common";
import sanitizeHtml from "sanitize-html";

import { OrganizationPageOutput } from "@/common/types/trpc";

type UpcomingEvents = OrganizationPageOutput["upcomingEvents"];
type EventRow = UpcomingEvents["rows"][number];

const EVENT_SCHEDULED = "https://schema.org/EventScheduled";
const OFFLINE_ATTENDANCE = "https://schema.org/OfflineEventAttendanceMode";
const IN_STOCK = "https://schema.org/InStock";
const SOLD_OUT = "https://schema.org/SoldOut";

const stripHtml = (html: string | null | undefined): string | undefined =>
  html ? sanitizeHtml(html, { allowedTags: [] }) || undefined : undefined;

const buildOffer = (row: EventRow, url: string) =>
  row.isSalesClosed && !row.isSoldOut
    ? {}
    : { offers: { "@type": "Offer" as const, url, availability: row.isSoldOut ? SOLD_OUT : IN_STOCK } };

export const buildEventsJsonLd = ({
  upcomingEvents,
  locale,
  organizer,
}: {
  upcomingEvents: UpcomingEvents;
  locale: Locales;
  organizer: { name: string | undefined; url: string };
}) =>
  upcomingEvents.events.flatMap((event) => {
    const rows = upcomingEvents.rows.filter((row) => row.ticketingId === event.id);
    const [firstRow] = rows;
    const lastRow = rows.at(-1);
    if (!firstRow || !lastRow) {
      return [];
    }
    const earliestStartRow = rows.reduce((earliest, row) =>
      new Date(row.startUtc).getTime() < new Date(earliest.startUtc).getTime() ? row : earliest
    );

    const eventField = getField(event.ticketingFields, locale);
    const name = eventField?.title;
    const description = stripHtml(eventField?.description);
    const place = event.address
      ? {
          location: { "@type": "Place" as const, name: event.address, address: event.address },
          eventAttendanceMode: OFFLINE_ATTENDANCE,
        }
      : {};
    const shared = {
      "@type": "Event" as const,
      name,
      description,
      eventStatus: EVENT_SCHEDULED,
      organizer: { "@type": "NGO" as const, ...organizer },
      ...place,
      ...(event.bannerUrl && { image: event.bannerUrl }),
    };

    return [
      {
        ...shared,
        url: event.url,
        startDate: earliestStartRow.startUtc,
        endDate: lastRow.endUtc,
        ...(rows.length > 1
          ? {
              subEvent: rows.map((row) => ({
                ...shared,
                url: event.url,
                startDate: row.startUtc,
                endDate: row.endUtc,
                ...buildOffer(row, event.url),
              })),
            }
          : buildOffer(firstRow, event.url)),
      },
    ];
  });
